import {
  addDays,
  addMonths,
  addYears,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  setMonth,
  setYear,
  startOfMonth,
  startOfToday,
  startOfWeek,
} from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { isoToDate, isoWithin } from '@/lib/date-entry';
import { useTranslation } from '@/lib/i18n';
import { cn, toIsoDate } from '@/lib/utils';
import { Select } from './select';

const WEEK = { weekStartsOn: 1 } as const;
const MONTHS = Array.from({ length: 12 }, (_, index) => format(new Date(2000, index, 1), 'MMMM'));
const WEEKDAYS = Array.from({ length: 7 }, (_, index) =>
  format(addDays(startOfWeek(new Date(2000, 0, 3), WEEK), index), 'EEEEEE'),
);
const DAY_STEPS: Record<string, number> = { ArrowUp: -7, ArrowDown: 7 };

function clamp(date: Date, min: Date | null, max: Date | null): Date {
  if (min && date < min) return min;
  if (max && date > max) return max;
  return date;
}

const headerSelect =
  'h-7 w-auto rounded-md border-transparent bg-transparent py-0 pe-1.5 ps-2 text-sm font-medium shadow-none hover:border-input hover:bg-accent/50';

export function Calendar({
  value,
  onSelect,
  onClear,
  min,
  max,
  autoFocus,
}: {
  value: string;
  onSelect: (iso: string) => void;
  onClear?: () => void;
  min?: string;
  max?: string;
  autoFocus?: boolean;
}) {
  const common = useTranslation('common');
  const selected = value ? isoToDate(value) : null;
  const minDate = min ? isoToDate(min) : null;
  const maxDate = max ? isoToDate(max) : null;
  const today = startOfToday();
  const [cursor, setCursor] = useState(() => clamp(selected ?? today, minDate, maxDate));
  const gridRef = useRef<HTMLDivElement>(null);
  const gridHasFocus = useRef(Boolean(autoFocus));

  useEffect(() => {
    if (!gridHasFocus.current) return;
    gridRef.current?.querySelector<HTMLButtonElement>('[data-cursor="true"]')?.focus();
  }, [cursor]);

  const visibleMonth = startOfMonth(cursor);
  const firstCell = startOfWeek(visibleMonth, WEEK);
  const days = Array.from({ length: 42 }, (_, index) => addDays(firstCell, index));
  const firstYear = minDate?.getFullYear() ?? today.getFullYear() - 120;
  const lastYear = maxDate?.getFullYear() ?? today.getFullYear() + 20;
  const years = Array.from({ length: lastYear - firstYear + 1 }, (_, index) => lastYear - index);
  const allowed = (day: Date) => isoWithin(toIsoDate(day), min, max);
  const moveTo = (date: Date) => setCursor(clamp(date, minDate, maxDate));

  const onGridKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const rtl = getComputedStyle(event.currentTarget).direction === 'rtl';
    const horizontal = event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0;
    let next: Date | null = null;
    if (horizontal) next = addDays(cursor, rtl ? -horizontal : horizontal);
    else if (DAY_STEPS[event.key]) next = addDays(cursor, DAY_STEPS[event.key]);
    else if (event.key === 'PageUp')
      next = event.shiftKey ? addYears(cursor, -1) : addMonths(cursor, -1);
    else if (event.key === 'PageDown')
      next = event.shiftKey ? addYears(cursor, 1) : addMonths(cursor, 1);
    else if (event.key === 'Home') next = startOfWeek(cursor, WEEK);
    else if (event.key === 'End') next = endOfWeek(cursor, WEEK);
    if (!next) return;
    event.preventDefault();
    moveTo(next);
  };

  return (
    <div className="w-[17.5rem] select-none">
      <div className="mb-2 flex items-center justify-between gap-1">
        <button
          type="button"
          onClick={() => moveTo(addMonths(cursor, -1))}
          aria-label={common.t('calendar.previous_month')}
          className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-ring"
        >
          <ChevronLeft className="h-4 w-4 rtl:-scale-x-100" />
        </button>
        <div className="flex items-center">
          <Select
            aria-label="Month"
            value={cursor.getMonth()}
            onChange={(event) => moveTo(setMonth(cursor, Number(event.target.value)))}
            className={headerSelect}
            contentClassName="min-w-[9rem]"
          >
            {MONTHS.map((name, index) => (
              <option key={name} value={index}>
                {name}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Year"
            value={cursor.getFullYear()}
            onChange={(event) => moveTo(setYear(cursor, Number(event.target.value)))}
            className={cn(headerSelect, 'tabular-nums')}
            contentClassName="min-w-[6rem]"
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Select>
        </div>
        <button
          type="button"
          onClick={() => moveTo(addMonths(cursor, 1))}
          aria-label={common.t('calendar.next_month')}
          className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-ring"
        >
          <ChevronRight className="h-4 w-4 rtl:-scale-x-100" />
        </button>
      </div>

      <div
        role="grid"
        aria-label={format(visibleMonth, 'MMMM yyyy')}
        ref={gridRef}
        onKeyDown={onGridKey}
        onFocus={() => {
          gridHasFocus.current = true;
        }}
        onBlur={(event) => {
          if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) {
            gridHasFocus.current = false;
          }
        }}
      >
        <div role="row" className="mb-1 grid grid-cols-7">
          {WEEKDAYS.map((name) => (
            <span
              key={name}
              role="columnheader"
              className="py-1 text-center text-[11px] font-medium text-muted-foreground"
            >
              {name}
            </span>
          ))}
        </div>
        {Array.from({ length: 6 }, (_, week) => (
          <div role="row" key={week} className="grid grid-cols-7 gap-y-0.5">
            {days.slice(week * 7, week * 7 + 7).map((day) => {
              const isSelected = selected ? isSameDay(day, selected) : false;
              const isToday = isSameDay(day, today);
              const inMonth = isSameMonth(day, visibleMonth);
              const isCursor = isSameDay(day, cursor);
              const enabled = allowed(day);
              return (
                <div
                  role="gridcell"
                  aria-selected={isSelected}
                  key={toIsoDate(day)}
                  className="grid place-items-center"
                >
                  <button
                    type="button"
                    tabIndex={isCursor ? 0 : -1}
                    data-cursor={isCursor}
                    disabled={!enabled}
                    aria-current={isToday ? 'date' : undefined}
                    aria-label={format(day, 'EEEE, d MMMM yyyy')}
                    onClick={() => onSelect(toIsoDate(day))}
                    onFocus={() => !isCursor && setCursor(day)}
                    className={cn(
                      'relative h-9 w-9 rounded-md text-sm tabular-nums transition-colors focus-ring',
                      inMonth ? 'text-foreground' : 'text-muted-foreground/45',
                      isSelected
                        ? 'bg-primary font-semibold text-primary-foreground shadow-sm hover:bg-primary/90'
                        : 'hover:bg-accent hover:text-accent-foreground',
                      isToday && !isSelected && 'font-semibold text-primary',
                      isToday &&
                        !isSelected &&
                        'after:absolute after:bottom-1 after:left-1/2 after:h-1 after:w-1 after:-translate-x-1/2 after:rounded-full after:bg-primary',
                      !enabled && 'cursor-not-allowed opacity-35 hover:bg-transparent',
                    )}
                  >
                    {day.getDate()}
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="mt-2 flex items-center justify-between border-t pt-2">
        <button
          type="button"
          disabled={!allowed(today)}
          onClick={() => onSelect(toIsoDate(today))}
          className="rounded-md px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/10 focus-ring disabled:opacity-40"
        >
          Today
        </button>
        {onClear && (
          <button
            type="button"
            onClick={onClear}
            className="rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-ring"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}
